import { useCallback, useEffect, useState } from "react";
import { globalStore } from "../utils/globalStore";
import { UserRoleManager } from "../utils/userRole";

/**
 * 与 Home 页一致的剧集相关按钮权限（globalStore 权限 + 菜单 meta.perms）。
 * 供 Home 等页面复用，避免复制大段权限判断。
 */
export const useSeriesButtonPermissions = () => {
  const [userPermissions, setUserPermissions] = useState<string[]>(
    () => globalStore.getUserInfo()?.permissions || []
  );
  const [menuListState, setMenuListState] = useState<any[]>(() => globalStore.getMenuList() || []);

  useEffect(() => {
    return globalStore.subscribe(() => {
      const user = globalStore.getUserInfo();
      setUserPermissions(user?.permissions || []);
      setMenuListState(globalStore.getMenuList() || []);
    });
  }, []);

  const checkSeriesButtonPermission = useCallback(
    (permission: string): boolean => {
      const currentRole = UserRoleManager.getCurrentUserRole();
      if (currentRole?.isAdmin) {
        return true;
      }
      if (userPermissions?.length && userPermissions.includes(permission)) {
        return true;
      }
      const menuList = menuListState;
      if (!menuList?.length) {
        return false;
      }
      const findMenuPermission = (menus: any[], targetPermission: string): boolean => {
        for (const menu of menus) {
          if (menu.meta?.perms === targetPermission) {
            return true;
          }
          if (menu.children?.length) {
            if (findMenuPermission(menu.children, targetPermission)) {
              return true;
            }
          }
        }
        return false;
      };
      return findMenuPermission(menuList, permission);
    },
    [userPermissions, menuListState]
  );

  const hasSeriesDeletePermission = useCallback(
    () => checkSeriesButtonPermission("series:delete"),
    [checkSeriesButtonPermission]
  );

  const hasCreateSeriesPermission = useCallback(
    () => checkSeriesButtonPermission("system:series:create"),
    [checkSeriesButtonPermission]
  );

  const isAdminOrSuperAdmin = useCallback((): boolean => {
    const currentRole = UserRoleManager.getCurrentUserRole();
    if (currentRole?.isAdmin) {
      return true;
    }
    const userInfo = globalStore.getUserInfo();
    if (userInfo?.roles) {
      const roleKeys = userInfo.roles.map((role: any) =>
        (typeof role === "string" ? role : role.role_key || role.code || "").toLowerCase()
      );
      return roleKeys.some(
        (key: string) => key === "super_admin" || key === "admin" || key === "administrator"
      );
    }
    return false;
  }, []);

  return {
    checkSeriesButtonPermission,
    hasSeriesDeletePermission,
    hasCreateSeriesPermission,
    isAdminOrSuperAdmin,
  };
};
